import { useEffect, useState } from 'react';
import { IProductHolder, getUsageLines } from '../../Services/productEntitlements';

/** Les produits qui produisent une ligne de dépassement (Store, Track — serveurs). */
export const PRODUCTS_WITH_OVERAGE = ['store', 'track_server'];

/** Bornes UTC du mois, `offset` relatif au mois courant (-1 = mois dernier). */
export const monthBounds = (offset: number): { from: string; to: string } => {
	const now = new Date();
	const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
	const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 1));
	return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
};

/**
 * Les dépassements du produit sur une période, compte par compte — à la demande.
 *
 * ⚠️ Un appel par compte : le calcul relit la consommation journalière et la
 * grille Stripe. Il ne part donc PAS au chargement de l'écran, et il est
 * séquentiel — trente comptes en parallèle sur ce calcul, c'est api-billing au
 * tapis pendant qu'on recette.
 */
/** Ce qui serait facturé à un compte sur la période, et le détail par ligne. */
export interface IOverage {
	cents: number
	/** Le libellé de chaque ligne — il porte les volumes (« +12.4 GB stored »). */
	labels: string[]
}

export const useProductOverages = (product: string, holders: IProductHolder[], monthOffset: number) => {
	const [amounts, setAmounts] = useState<Record<string, IOverage> | null>(null);
	const [done, setDone] = useState(0);
	const [running, setRunning] = useState(false);

	// ⚠️ Changer de mois invalide les montants : affichés sous une autre période,
	// ils se lisent comme le dépassement de celle-ci. Mieux vaut rien que faux.
	useEffect(() => {
		setAmounts(null);
		setDone(0);
	}, [product, monthOffset]);

	const compute = async () => {
		const { from, to } = monthBounds(monthOffset);
		setRunning(true);
		setDone(0);
		const acc: Record<string, IOverage> = {};
		for (const h of holders) {
			try {
				const lines = (await getUsageLines(h.billingAccountId, from, to)).lines ?? [];
				const mine = lines.filter((l) => l.product === product);
				acc[h.billingAccountId] = {
					cents: mine.reduce((sum, l) => sum + l.amountCents, 0),
					labels: mine.map((l) => l.label),
				};
			} catch {
				// Le compte reste sans montant : une erreur ne vaut pas 0 €.
			}
			setDone((n) => n + 1);
			setAmounts({ ...acc });
		}
		setRunning(false);
	};

	return { amounts, done, running, compute };
};
